import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListBulletIcon, ViewColumnsIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '@material-tailwind/react';
import TaskWorkspaceShell from './TaskWorkspaceShell';
import TaskPageHeader from './TaskPageHeader';
import TaskTabsBar from './TaskTabsBar';
import TaskList from './TaskList';
import TaskBoard from './TaskBoard';
import CreateTaskModal from './CreateTaskModal';

const TABS = [
    { id: 'list', label: 'List', icon: ListBulletIcon, description: 'List view' },
    { id: 'board', label: 'Board', icon: ViewColumnsIcon, description: 'Board view' },
];

const TasksPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
    const [reloadTrigger, setReloadTrigger] = useState(0);
    const debounceRef = useRef(null);

    const view = searchParams.get('view') || 'list';
    const scope = searchParams.get('scope') || 'all';
    const status = searchParams.get('status') || 'all';
    const priority = searchParams.get('priority') || 'all';
    const sort = searchParams.get('sort') || 'created_desc';
    const search = searchParams.get('search') || '';

    const setParam = useCallback((key, value) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (value) {
                next.set(key, value);
            } else {
                next.delete(key);
            }
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const clearFilters = useCallback(() => {
        const currentView = searchParams.get('view') || 'list';
        setSearchParams({ view: currentView }, { replace: true });
        setSearchInput('');
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setParam('search', searchInput);
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [searchInput, setParam]);

    const hasActiveFilters = scope !== 'all' || status !== 'all' || priority !== 'all' || search !== '';

    const filters = { scope, status, priority, sort, search };
    const filterSetters = {
        setScope: v => setParam('scope', v === 'all' ? '' : v),
        setStatus: v => setParam('status', v === 'all' ? '' : v),
        setPriority: v => setParam('priority', v === 'all' ? '' : v),
        setSort: v => setParam('sort', v === 'created_desc' ? '' : v),
        setSearchInput,
        searchInput,
        clearFilters,
        hasActiveFilters,
    };

    const handleTaskCreated = useCallback(() => {
        setShowCreateModal(false);
        setReloadTrigger(n => n + 1);
    }, []);

    return (
        <TaskWorkspaceShell>
            <TaskPageHeader
                title="Tasks"
                subtitle="Manage and track your tasks across the workspace."
                actions={
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#E64A2E]"
                        size="sm"
                    >
                        <PlusIcon className="h-4 w-4" />
                        New Task
                    </Button>
                }
            />

            <TaskTabsBar
                tabs={TABS}
                activeTab={view}
                onChange={id => setParam('view', id)}
            />

            {view === 'board' ? (
                <TaskBoard
                    filters={filters}
                    filterSetters={filterSetters}
                    reloadTrigger={reloadTrigger}
                    onCreateTask={() => setShowCreateModal(true)}
                    onTaskCreated={handleTaskCreated}
                />
            ) : (
                <TaskList
                    filters={filters}
                    filterSetters={filterSetters}
                    reloadTrigger={reloadTrigger}
                    onCreateTask={() => setShowCreateModal(true)}
                />
            )}

            {showCreateModal && (
                <CreateTaskModal
                    open={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onTaskCreated={handleTaskCreated}
                />
            )}
        </TaskWorkspaceShell>
    );
};

export default TasksPage;
